import React, { useMemo } from 'react';
import { getPaginationItems } from '../../utils/paginationRange';

const btnBase =
  'min-w-[2.25rem] px-2.5 py-1 rounded border text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

export default function NumberedPagination({
  page,
  totalPages,
  onPageChange,
  prevLabel = 'Previous',
  nextLabel = 'Next',
  maxPageButtons = 10,
  summary,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  pageSizeLabel,
  className = '',
}) {
  const items = useMemo(
    () => getPaginationItems(page, totalPages, { maxPageButtons }),
    [page, totalPages, maxPageButtons],
  );

  if (totalPages < 1) return null;

  const goTo = (next) => {
    const clamped = Math.max(1, Math.min(totalPages, next));
    if (clamped !== page) onPageChange(clamped);
  };

  return (
    <div
      className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t border-dark-border text-sm text-dark-text-secondary ${className}`}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 shrink-0">
        {summary != null ? <span>{summary}</span> : null}
        {pageSizeOptions?.length > 0 && onPageSizeChange && (
          <label className="flex items-center gap-2 text-xs text-dark-text-secondary">
            <span className="whitespace-nowrap">{pageSizeLabel}</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="rounded-md border border-dark-border bg-dark-bg text-dark-text text-xs py-1.5 pl-2 pr-7 focus:border-primary/60 focus:ring-1 focus:ring-primary/35 outline-none cursor-pointer"
              aria-label={pageSizeLabel}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <nav className="flex flex-wrap items-center justify-end gap-1.5" aria-label="Pagination">
        <button
          type="button"
          className={`${btnBase} border-dark-border hover:border-primary hover:text-primary`}
          onClick={() => goTo(page - 1)}
          disabled={page <= 1}
        >
          {prevLabel}
        </button>
        {items.map((item, index) => {
          if (item === 'ellipsis') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="px-1 text-dark-text-secondary select-none"
                aria-hidden
              >
                …
              </span>
            );
          }
          const isActive = item === page;
          return (
            <button
              key={item}
              type="button"
              aria-label={`Page ${item}`}
              aria-current={isActive ? 'page' : undefined}
              className={`${btnBase} ${
                isActive
                  ? 'border-primary bg-primary/15 text-primary font-semibold'
                  : 'border-dark-border text-dark-text hover:border-primary hover:text-primary'
              }`}
              onClick={() => goTo(item)}
            >
              {item}
            </button>
          );
        })}
        <button
          type="button"
          className={`${btnBase} border-dark-border hover:border-primary hover:text-primary`}
          onClick={() => goTo(page + 1)}
          disabled={page >= totalPages}
        >
          {nextLabel}
        </button>
      </nav>
    </div>
  );
}
